import { useCallback, useRef } from 'react';

/**
 * Route import map - maps route paths to their dynamic import functions.
 * These match the lazy() imports in App.tsx for consistent code splitting.
 *
 * @see GW-223: Route prefetching on hover/focus
 */
export const routeImports: Record<string, () => Promise<unknown>> = {
  // Main navigation
  '/marketplace/applications': () => import('@/pages/marketplace/ApplicationBrowsePage'),

  // Section landing routes (GW-12315): sidebar links point at the bare section
  // path (which redirects to the default tab), so warm the landing chunk on
  // hover/focus to match sibling coverage and avoid a cold full-screen fallback.
  '/organization': () => import('@/pages/organization/OrganizationSummaryPage'),
  '/personal': () => import('@/pages/personal/PersonalWorkspacePage'),
  '/publisher': () => import('@/pages/developer/PublisherWorkspacePage'),

  // Personal routes
  '/personal/sessions': () => import('@/pages/protected/MySessionsPage'),
  '/settings': () => import('@/pages/protected/SettingsPage'),

  // Organization routes
  '/organization/summary': () => import('@/pages/organization/OrganizationSummaryPage'),
  '/organization/members': () => import('@/pages/organization/OrgMembersRolePage'),
  '/organization/usage-analytics': () => import('@/pages/protected/UsageAnalyticsPage'),
  '/organization/tokens': () => import('@/pages/protected/TokensPage'),
  '/organization/transactions': () => import('@/pages/organization/OrganizationTransactionsPage'),
  // GW-8286: /developer/applications retired — /publisher/applications
  // (PublisherWorkspacePage's "applications" tab) is now the sole canonical
  // location; OrgApplicationsManagementPage is prefetched as part of that
  // shell's own bundle, not standalone here.
  '/publisher/applications': () => import('@/pages/applications/OrgApplicationsManagementPage'),
};

// Track already prefetched routes to avoid duplicate loads
const prefetchedRoutes = new Set<string>();

/**
 * Clear the prefetched routes cache.
 * Primarily used for testing purposes.
 */
export function clearPrefetchCache(): void {
  prefetchedRoutes.clear();
}

/**
 * Prefetch a route's code chunk without rendering.
 * Silently handles errors and prevents duplicate prefetches.
 *
 * @param path - The route path to prefetch
 * @param imports - Optional custom route imports map (for testing)
 */
export async function prefetchRoute(
  path: string,
  imports: Record<string, () => Promise<unknown>> = routeImports
): Promise<void> {
  // Skip if already prefetched
  if (prefetchedRoutes.has(path)) {
    return;
  }

  const importFn = imports[path];
  if (!importFn) {
    return;
  }

  try {
    // Mark as prefetched before starting to prevent race conditions
    prefetchedRoutes.add(path);
    await importFn();
  } catch {
    // Silently handle errors - prefetch is best-effort
    // Remove from prefetched set so it can be retried
    prefetchedRoutes.delete(path);
  }
}

/**
 * Hook to prefetch route chunks on hover/focus.
 * Debounces prefetch calls to avoid excessive loading.
 *
 * @param imports - Optional custom route imports map (for testing)
 * @returns Object with prefetch function
 *
 * @example
 * ```tsx
 * const { prefetch } = usePrefetch();
 *
 * <Link
 *   to="/marketplace/applications"
 *   onMouseEnter={() => prefetch('/marketplace/applications')}
 *   onFocus={() => prefetch('/marketplace/applications')}
 * >
 *   Marketplace
 * </Link>
 * ```
 *
 * @see GW-223: Route prefetching on hover/focus
 */
export function usePrefetch(imports: Record<string, () => Promise<unknown>> = routeImports) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingPathRef = useRef<string | null>(null);

  const prefetch = useCallback(
    (path: string) => {
      // Clear any pending prefetch
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Store the path we want to prefetch
      pendingPathRef.current = path;

      // Debounce the prefetch call (150ms)
      timeoutRef.current = setTimeout(() => {
        if (pendingPathRef.current) {
          prefetchRoute(pendingPathRef.current, imports);
          pendingPathRef.current = null;
        }
      }, 150);
    },
    [imports]
  );

  return { prefetch };
}
